import React, { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { GalleryData, GalleryItem } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import styles from './GallerySection.module.scss';

const TITLE_PLACEHOLDER = 'Enter title here';
const DESC_PLACEHOLDER = 'Enter description here';

interface GallerySectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): GalleryData {
  const images = Array.isArray(data.images) ? (data.images as string[]) : [];
  const items = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        url: typeof i.url === 'string' ? i.url : '',
        title: typeof i.title === 'string' ? i.title : '',
        description: typeof i.description === 'string' ? i.description : '',
      }))
    : [];

  return {
    title: typeof data.title === 'string' ? data.title : 'Gallery',
    images,
    items,
  };
}

export function GallerySectionEditor({
  data,
  onUpdateData,
}: GallerySectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(-1);

  const normalizedItems = useMemo(() => {
    const base = d.items ?? [];
    if (base.length > 0) return base;
    if ((d.images ?? []).length > 0) {
      return (d.images ?? []).map((url) => ({
        url,
        title: '',
        description: '',
      }));
    }
    return [];
  }, [d.images, d.items]);

  const updateItem = (index: number, patch: Partial<GalleryItem>) => {
    const next = [...normalizedItems];
    if (!next[index]) next[index] = { url: '', title: '', description: '' };
    next[index] = { ...next[index], ...patch };
    onUpdateData({ ...data, items: next });
  };

  const addImage = () => {
    onUpdateData({
      ...data,
      items: [...normalizedItems, { url: '', title: '', description: '' }],
    });
  };

  const removeImage = (index: number) => {
    const next = normalizedItems.filter((_, i) => i !== index);
    onUpdateData({ ...data, items: next });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;
    const index = uploadIndexRef.current;
    compressImageFile(file)
      .then((imageUrl) => {
        if (index >= 0) {
          updateItem(index, { url: imageUrl });
        } else {
          onUpdateData({
            ...data,
            items: [
              ...normalizedItems,
              { url: imageUrl, title: '', description: '' },
            ],
          });
        }
      })
      .catch(() => {});
    e.target.value = '';
    uploadIndexRef.current = -1;
  };

  const triggerImageUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const triggerAddImageUpload = () => {
    uploadIndexRef.current = -1;
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
        aria-label="Upload image"
      />
      <input
        type="text"
        className={styles.titleInput}
        value={d.title}
        onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
        placeholder="Section title"
      />
      <div className={styles.grid}>
        {normalizedItems.map((item, i) => (
          <div key={i} className={`${styles.item} ${styles.editorItem}`}>
            <div className={styles.editorItemHeader}>
              <p className={styles.editorItemTitle}>Image {i + 1}</p>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeImage(i)}
              >
                Remove
              </button>
            </div>
            <div className={styles.editorThumbRow}>
              <div className={styles.editorThumb}>
                <img
                  src={item.url?.trim() ? item.url : galleryPlaceholder}
                  alt=""
                />
              </div>
              <div className={styles.editorFields}>
                <div className={styles.imageUrlRow}>
                  <input
                    type="text"
                    value={item.url ?? ''}
                    onChange={(e) => updateItem(i, { url: e.target.value })}
                    placeholder="Image URL"
                  />
                  <button
                    type="button"
                    className={styles.browseBtn}
                    onClick={() => triggerImageUpload(i)}
                  >
                    Browse
                  </button>
                  <button
                    type="button"
                    className={styles.clearBtn}
                    onClick={() => updateItem(i, { url: '' })}
                  >
                    Clear
                  </button>
                </div>
                <input
                  type="text"
                  value={item.title ?? ''}
                  onChange={(e) => updateItem(i, { title: e.target.value })}
                  placeholder={TITLE_PLACEHOLDER}
                />
                <textarea
                  value={item.description ?? ''}
                  onChange={(e) =>
                    updateItem(i, { description: e.target.value })
                  }
                  placeholder={DESC_PLACEHOLDER}
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.addRow}>
        <button type="button" className={styles.addBtn} onClick={addImage}>
          + Add image (URL)
        </button>
        <button
          type="button"
          className={styles.uploadBtn}
          onClick={triggerAddImageUpload}
        >
          Upload image
        </button>
      </div>
    </>
  );
}

interface GallerySectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function GallerySection({ data }: GallerySectionProps) {
  const d = getData(data);
  const [previewItem, setPreviewItem] = useState<{
    url: string;
    title: string;
    description: string;
  } | null>(null);

  const previewItems = useMemo(() => {
    const items =
      (d.items ?? []).length > 0
        ? (d.items ?? [])
        : (d.images ?? []).map((url) => ({ url, title: '', description: '' }));

    if (items.length === 0) {
      return [
        { url: galleryPlaceholder, title: '', description: '' },
        { url: galleryPlaceholder, title: '', description: '' },
        { url: galleryPlaceholder, title: '', description: '' },
        { url: galleryPlaceholder, title: '', description: '' },
        { url: galleryPlaceholder, title: '', description: '' },
        { url: galleryPlaceholder, title: '', description: '' },
      ];
    }

    return items.map((it) => ({
      url: it.url && it.url.trim() ? it.url : galleryPlaceholder,
      title: it.title ?? '',
      description: it.description ?? '',
    }));
  }, [d.images, d.items]);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{d.title}</h2>
        <div className={styles.grid}>
          {previewItems.map((item, i) => (
            <div key={i} className={`${styles.item} ${styles.previewItem}`}>
              <button
                type="button"
                className={styles.previewImageWrap}
                onClick={() =>
                  setPreviewItem({
                    url: item.url,
                    title: item.title,
                    description: item.description,
                  })
                }
                aria-label="View full image"
              >
                <img src={item.url} alt="" className={styles.image} />
                <div className={styles.previewOverlay} />
                <div className={styles.previewOverlayContent}>
                  <div
                    className={`${styles.previewTitle} ${!item.title.trim() ? styles.placeholderText : ''}`}
                  >
                    {item.title.trim() ? item.title : TITLE_PLACEHOLDER}
                  </div>
                  <div
                    className={`${styles.previewDesc} ${!item.description.trim() ? styles.placeholderText : ''}`}
                  >
                    {item.description.trim()
                      ? item.description
                      : DESC_PLACEHOLDER}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
      <Modal
        show={previewItem != null}
        onHide={() => setPreviewItem(null)}
        centered
        size="xl"
        className={styles.previewModal}
      >
        <Modal.Body className={styles.previewModalBody}>
          {previewItem && (
            <div className={styles.previewModalContent}>
              <div className={styles.previewModalCaption}>
                <div
                  className={`${styles.previewModalTitle} ${!previewItem.title.trim() ? styles.previewModalPlaceholder : ''}`}
                >
                  {previewItem.title.trim()
                    ? previewItem.title
                    : TITLE_PLACEHOLDER}
                </div>
                <div
                  className={`${styles.previewModalDesc} ${!previewItem.description.trim() ? styles.previewModalPlaceholder : ''}`}
                >
                  {previewItem.description.trim()
                    ? previewItem.description
                    : DESC_PLACEHOLDER}
                </div>
              </div>
              <img
                src={previewItem.url}
                alt="Preview"
                className={styles.previewModalImage}
              />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </section>
  );
}

export default GallerySection;
